import {
  CalendarDays,
  Compass,
  Heart,
  Lock,
  MapPin,
  ShieldCheck,
  UtensilsCrossed,
} from "lucide-react";
import { Reveal } from "./Reveal";

const IconChip = ({ icon: Icon, gold = false }) => (
  <span
    className={`flex h-11 w-11 items-center justify-center rounded-full border ${
      gold
        ? "border-crave-gold/30 bg-crave-gold/15 text-crave-gold"
        : "border-white/10 bg-crave-red/25 text-crave-cream"
    }`}
  >
    <Icon className="h-5 w-5" aria-hidden="true" />
  </span>
);

const Card = ({ testId, className = "", children }) => (
  <div
    data-testid={testId}
    className={`group relative overflow-hidden rounded-3xl border border-crave-border bg-crave-card transition-colors duration-500 hover:border-crave-gold/40 ${className}`}
  >
    {children}
  </div>
);

const Bubble = ({ icon: Icon, text, testId, className = "", delay = 0 }) => (
  <span
    data-testid={testId}
    style={{ animationDelay: `${delay}s` }}
    className={`fc-bubble pointer-events-none z-20 inline-flex items-center gap-2 rounded-2xl border border-crave-gold/30 bg-[#241c16]/80 px-3 py-1.5 text-[11px] font-medium text-crave-cream shadow-[0_10px_30px_-10px_rgba(201,162,75,0.45)] backdrop-blur-md md:absolute md:px-3.5 md:py-2 md:text-xs ${className}`}
  >
    <Icon className="h-3.5 w-3.5 shrink-0 text-crave-gold" aria-hidden="true" />
    {text}
  </span>
);

// Image shown at its natural aspect ratio (no cropping); bubbles float in the
// image's negative space on desktop and sit in a static row below on mobile.
const ImageArea = ({ src, alt, testId, className = "", children }) => (
  <div className={`relative flex flex-col md:block ${className}`}>
    <img
      src={src}
      alt={alt}
      loading="lazy"
      data-testid={testId}
      className="block h-auto w-full"
    />
    <div className="flex flex-wrap content-start gap-2 p-4 md:contents">{children}</div>
  </div>
);

export const Features = () => (
  <section id="features" data-testid="features-section" className="scroll-mt-24 py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <Reveal>
        <p className="text-xs font-bold uppercase tracking-[0.28em] text-crave-gold">Why Foodie Crave</p>
        <h2 className="mt-4 max-w-3xl font-display text-4xl font-medium leading-[1.05] tracking-tight text-crave-cream sm:text-5xl">
          Seek out new spots locally or at your next destination. Remember what you love. Enjoy the experience.
        </h2>
        <p className="mt-5 max-w-xl text-base leading-relaxed text-crave-muted">
          A dining experience app with restaurant discovery built in — made for solo diners, date
          nights, family meals, and outings with friends alike.
        </p>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8">
        <div className="flex flex-col gap-6 max-md:contents md:col-span-7 md:gap-8">
          <Reveal className="order-1">
            <Card testId="feature-card-discover">
              <ImageArea
                src="/images/features/discover.png"
                alt="Foodie Crave Discover screen showing nearby restaurants on a map"
                testId="feature-discover-image"
              >
                <Bubble
                  icon={MapPin}
                  text="Find something new to crave."
                  testId="bubble-discover-1"
                  className="md:right-[6%] md:top-[5%]"
                  delay={0}
                />
                <Bubble
                  icon={UtensilsCrossed}
                  text="Cafés, diners, and hidden gems."
                  testId="bubble-discover-2"
                  className="md:left-[6%] md:top-[60%]"
                  delay={1.7}
                />
                <Bubble
                  icon={Compass}
                  text="Search by name, keyword, or city."
                  testId="bubble-discover-3"
                  className="md:bottom-[5%] md:left-[8%]"
                  delay={3.2}
                />
              </ImageArea>
            </Card>
          </Reveal>

          <Reveal className="order-4" delay={0.1}>
            <Card testId="feature-card-invite">
              <div className="relative">
                <img
                  src="/images/features/invite.png"
                  alt="Foodie Crave Friends screen in a sealed invitation envelope on a candlelit dinner table"
                  loading="lazy"
                  data-testid="feature-invite-image"
                  className="block h-auto w-full"
                />
                <span
                  data-testid="feature-invite-badge"
                  className="absolute bottom-4 right-4 rounded-full border border-crave-gold/30 bg-[#241c16]/80 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-crave-gold backdrop-blur-md"
                >
                  Optional
                </span>
              </div>
            </Card>
          </Reveal>
        </div>

        <div className="flex flex-col gap-6 max-md:contents md:col-span-5 md:gap-8">
          <Reveal className="order-2" delay={0.05}>
            <Card testId="feature-card-remember">
              <ImageArea
                src="/images/features/remember.png"
                alt="Foodie Crave My Places saved-restaurants list beside a handwritten restaurants-to-try notebook"
                testId="feature-remember-image"
              >
                <Bubble
                  icon={Heart}
                  text="Your favorites, all in one place."
                  testId="bubble-remember-1"
                  className="md:bottom-[6%] md:left-[6%]"
                  delay={0.9}
                />
              </ImageArea>
            </Card>
          </Reveal>

          <Reveal className="order-3" delay={0.1}>
            <Card testId="feature-card-experience">
              <ImageArea
                src="/images/features/experience.png"
                alt="Foodie Crave Visited dining history list held outside a warmly lit restaurant"
                testId="feature-experience-image"
              >
                <Bubble
                  icon={CalendarDays}
                  text="Every visit becomes part of your story."
                  testId="bubble-experience-1"
                  className="md:bottom-[6%] md:left-[6%]"
                  delay={1.3}
                />
              </ImageArea>
            </Card>
          </Reveal>
        </div>

        <Reveal className="order-5 md:col-span-12" delay={0.15}>
          <Card
            testId="feature-card-private"
            className="flex flex-col gap-6 p-8 md:flex-row md:items-center md:justify-between md:p-10"
          >
            <div>
              <IconChip icon={ShieldCheck} />
              <h3 className="mt-6 font-display text-2xl font-medium tracking-tight text-crave-cream sm:text-3xl md:mt-4">
                Private by default
              </h3>
              <p className="mt-1.5 text-base text-crave-muted">
                Your dining memories belong to you.
              </p>
            </div>
            <Bubble
              icon={Lock}
              text="No public feeds or follower counts."
              testId="bubble-private-1"
              className="md:shrink-0 md:[position:static]"
              delay={1.1}
            />
          </Card>
        </Reveal>
      </div>
    </div>
  </section>
);
